/* Brutalist Portfolio — strict 3-col grid with technical metadata
   under each frame. Monospace captions. Hard amber section rules. */

import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getFocalStyle } from "@/lib/focal";
import { optimizeImageUrl } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const SPECS = [
  { iso: "100", f: "1.8", shutter: "1/250" },
  { iso: "200", f: "2.8", shutter: "1/500" },
  { iso: "400", f: "4.0", shutter: "1/1000" },
  { iso: "800", f: "5.6", shutter: "1/2000" },
];

export default function BrutalistPortfolio() {
  const { settings, loading } = useSiteSettings();
  const port = settings?.portfolio_images || [];
  const focals = settings?.portfolio_focal_points || [];
  const studioName = settings?.studio_name || "Studio";
  useEffect(() => { document.title = `${studioName} // FRAMES`; }, [studioName]);
  if (loading && !settings) return <EditorialSkeleton eyebrow="// loading frames" />;

  return (
    <div data-testid="brutalist-portfolio"
      style={{ background: "var(--tenant-bg, #0C0A09)", color: "var(--tenant-fg, #FAFAF8)", fontFamily: "var(--tenant-sans, monospace)" }}>

      <section className="px-6 lg:px-12 pt-16 pb-10">
        <div className="max-w-[1480px] mx-auto">
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            // FRAMES.IDX
          </div>
          <div className="flex items-center gap-6 mt-4 mb-12 pb-4 border-b" style={{ borderColor: "var(--tenant-accent)" }}>
            <h1 className="leading-[0.85] uppercase"
                style={{ fontFamily: "var(--tenant-serif, monospace)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
              {settings?.portfolio_title || "FRAMES_INDEX"}
            </h1>
            <div className="flex-1 h-px" style={{ background: "var(--tenant-accent)" }} />
            <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)" }}>
              [COUNT] : {String(port.length).padStart(3, "0")}
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12 pb-32 max-w-[1480px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {port.map((src, i) => {
            const spec = SPECS[i % SPECS.length];
            return (
              <figure key={i}>
                <div className="relative">
                  <img src={optimizeImageUrl(src, { w: 900 })} alt=""
                    className="w-full aspect-square object-cover"
                    style={getFocalStyle(focals, i)}
                    loading={i < 3 ? "eager" : "lazy"} decoding="async" />
                  <div className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.32em] px-2 py-1"
                       style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
                    FRAME_{String(i + 1).padStart(3, "0")}.RAW
                  </div>
                </div>
                <div className="text-[9px] uppercase tracking-[0.32em] mt-2 grid grid-cols-3 gap-2"
                     style={{ color: "var(--tenant-fg-soft)" }}>
                  <span>ISO {spec.iso}</span>
                  <span>F/{spec.f}</span>
                  <span>{spec.shutter}s</span>
                </div>
              </figure>
            );
          })}
        </div>
        {port.length === 0 && (
          <p className="text-center text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)" }}>
            // no frames in buffer
          </p>
        )}
      </div>
    </div>
  );
}
